import React from "react";
import { BrowserRouter } from "react-router-dom";
import { ThemeProvider } from "@emotion/react";
import { customTheme } from "./components/theme";
import Pages from "./pages";
import { Client, Provider, fetchExchange, subscriptionExchange } from "urql";
import { cacheExchange, Cache, QueryInput } from "@urql/exchange-graphcache";
import { createClient as createWSClient } from "graphql-ws";
import { SnackbarProvider } from "components/providers/snackbarProvider";
import {
  LoginMutation,
  LogoutMutation,
  DeleteAccountMutation,
  GoogleLoginMutation,
  GetVendorAvailabilityDocument,
  GetVendorAvailabilityQuery,
  MeDocument,
  MeQuery,
  MyInvitedBookingsDocument,
  MyInvitedBookingsQuery,
  RegisterAccountMutation,
  RegisterAccountWithVendorMutation,
  RequestBookingMutation,
  SetVendorAvailabilityMutation,
} from "generated/graphql";
import { track } from "analytics";
import { analyticsErrorExchange } from "analytics/urqlErrorExchange";
import i18n from "locales/i18n";
import { toLanguage } from "locales/constants";

function betterUpdateQuery<Result, Query>(
  cache: Cache,
  qi: QueryInput,
  result: any,
  fn: (r: Result, q: Query) => Query,
) {
  return cache.updateQuery(qi, (data) => fn(result, data as any) as any);
}

// Module scope: App re-renders rebuild the urql Client, and StrictMode
// double-mounts in dev — a single ws client avoids duplicate sockets.
// lazy means no socket is opened until a subscription is actually mounted.
const wsClient = createWSClient({
  url: (process.env.REACT_APP_API_URL as string).replace(/^http/, "ws"),
  lazy: true,
  retryAttempts: Infinity,
  shouldRetry: () => true,
});

// Cache these properties by their id, so that we can update them in the cache after mutations
const App: React.FC = () => {
  const client = new Client({
    url: process.env.REACT_APP_API_URL as string,
    exchanges: [
      cacheExchange({
        keys: {
          Account: (data) => String(data.id),
          Vendor: (data) => String(data.id),
          VendorAvailability: (data) => String(data.id),
          Booking: (data) => String(data.id),
          EVChargerQuote: (data) => String(data.id),
          ConversationView: (data) => String(data.id),
          MessageView: (data) => String(data.id),
          SupportTicketView: (data) => String(data.id),
          TicketCommentView: (data) => String(data.id),
          AdminVendorView: (data) => String(data.id),
          AdminUserView: (data) => String(data.id),
          // Embedded value types (no id) — not normalized.
          WeeklyAvailabilityType: () => null,
          DayHoursType: () => null,
          CustomDateType: () => null,
          PerDayHoursType: () => null,
          BookingAttendeeType: () => null,
          BookingAttachmentType: () => null,
          MessageAttachmentType: () => null,
          MessagesPage: () => null,
          MessageableCounterpart: () => null,
          QuoteVendorAvailability: () => null,
          BookingBusySlot: () => null,
          GoogleListing: () => null,
          ExpiringDocument: () => null,
          PrivatePortalSettingsResponse: () => null,
          SupportTicketResponse: () => null,
          TicketCommentResponse: () => null,
          AdminVendorPage: () => null,
          AdminUserPage: () => null,
          AdminSummary: () => null,
          AdminVendorResponse: () => null,
          AdminUserResponse: () => null,
        },
        updates: {
          Mutation: {
            googleLogin(_result, _, cache) {
              betterUpdateQuery<GoogleLoginMutation, MeQuery>(
                cache,
                { query: MeDocument },
                _result,
                (result, query) => {
                  if (result.googleLogin.errors) {
                    return query;
                  }

                  return {
                    me: result.googleLogin.account,
                  };
                },
              );

              // These updaters already run exactly once per successful
              // mutation, so conversions are recorded without touching any of
              // the call sites.
              if (!(_result as GoogleLoginMutation).googleLogin.errors) {
                track("login", { method: "google" });
              }
            },
            login(_result, _, cache) {
              betterUpdateQuery<LoginMutation, MeQuery>(
                cache,
                { query: MeDocument },
                _result,
                (result, query) => {
                  if (result.login.errors) {
                    return query;
                  }

                  return {
                    me: result.login.account,
                  };
                },
              );

              if (!(_result as LoginMutation).login.errors) {
                track("login", { method: "password" });
              }
            },
            registerAccount(_result, _, cache) {
              betterUpdateQuery<RegisterAccountMutation, MeQuery>(
                cache,
                { query: MeDocument },
                _result,
                (result, query) => {
                  if (result.registerAccount.errors) {
                    return query;
                  }

                  return {
                    me: result.registerAccount.account,
                  };
                },
              );

              if (
                !(_result as RegisterAccountMutation).registerAccount.errors
              ) {
                track("signup", { accountType: "user" });
              }
            },
            registerAccountWithVendor(_result, _, cache) {
              betterUpdateQuery<RegisterAccountWithVendorMutation, MeQuery>(
                cache,
                { query: MeDocument },
                _result,
                (result, query) => {
                  if (result.registerAccountWithVendor.errors) {
                    return query;
                  }

                  return {
                    me: result.registerAccountWithVendor.account,
                  };
                },
              );

              if (
                !(_result as RegisterAccountWithVendorMutation)
                  .registerAccountWithVendor.errors
              ) {
                track("signup", { accountType: "vendor" });
              }
            },
            logout(_result, _, cache) {
              betterUpdateQuery<LogoutMutation, MeQuery>(
                cache,
                { query: MeDocument },
                _result,
                () => ({ me: null }),
              );
            },
            setVendorAvailability(_result, _, cache) {
              betterUpdateQuery<
                SetVendorAvailabilityMutation,
                GetVendorAvailabilityQuery
              >(
                cache,
                { query: GetVendorAvailabilityDocument },
                _result,
                (result, query) => {
                  if (
                    result.setVendorAvailability.errors ||
                    !result.setVendorAvailability.availability
                  ) {
                    return query;
                  }

                  return {
                    vendorAvailability:
                      result.setVendorAvailability.availability,
                  };
                },
              );
            },
            deleteAccount(_result, _, cache) {
              betterUpdateQuery<DeleteAccountMutation, MeQuery>(
                cache,
                { query: MeDocument },
                _result,
                () => ({ me: null }),
              );
            },
            requestBooking(_result, args: any, cache) {
              // Show the new pending request on the user's calendar right away.
              betterUpdateQuery<RequestBookingMutation, MyInvitedBookingsQuery>(
                cache,
                { query: MyInvitedBookingsDocument },
                _result,
                (result, query) => {
                  const booking = result.requestBooking.booking;
                  if (result.requestBooking.errors || !booking || !query) {
                    return query;
                  }
                  return {
                    myInvitedBookings: [
                      ...(query.myInvitedBookings ?? []),
                      booking,
                    ],
                  };
                },
              );
              // The requested slot is now busy — refetch the picker data.
              cache.invalidate("Query", "vendorAvailabilityForQuote", {
                quoteId: args?.input?.quoteId,
              });

              if (!(_result as RequestBookingMutation).requestBooking.errors) {
                track("booking_requested");
              }
            },
          },
        },
      }),
      analyticsErrorExchange,
      fetchExchange,
      subscriptionExchange({
        forwardSubscription(request) {
          const input = { ...request, query: request.query || "" };
          return {
            subscribe: (sink) => ({
              unsubscribe: wsClient.subscribe(input, sink),
            }),
          };
        },
      }),
    ],
    // A function, so each request carries the language picked since.
    fetchOptions: () => ({
      credentials: "include",
      headers: { "X-Language": toLanguage(i18n.language) },
    }),
  });

  return (
    <Provider value={client}>
      <ThemeProvider theme={customTheme}>
        <SnackbarProvider>
          <BrowserRouter>
            <Pages />
          </BrowserRouter>
        </SnackbarProvider>
      </ThemeProvider>
    </Provider>
  );
};

export default App;
